import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import {
  HelpCircle,
  ChevronRight,
  Search,
  PlayCircle,
  Compass,
  BookOpen,
} from "lucide-react";
import { FAQ } from "../lib/helpContent";
import IntroStory from "./IntroStory";
import InteractiveTour from "./InteractiveTour";

export default function HelpDrawer({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [story, setStory] = useState(false);
  const [tour, setTour] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Feb 27 2026 · Multi-term matcher — see HelpPage.jsx rationale.
  // Splits on whitespace and requires EVERY token to appear somewhere
  // in q/a so "Roadmap and plan" doesn't collapse to zero results.
  const filtered = (() => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return FAQ.slice(0, 6); // top 6 in the drawer
    return FAQ.filter((f) => {
      const s = ((f.q || "") + " " + (f.a || "")).toLowerCase();
      return tokens.every((t) => s.includes(t));
    });
  })();

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg p-0 flex flex-col rounded-none border-l-2 border-neutral-900"
          data-testid="help-drawer"
        >
          <SheetHeader className="p-6 pb-4 border-b-2 border-neutral-900 bg-neutral-50">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#4A4458] text-white flex-shrink-0">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                  Help & resources
                </div>
                <SheetTitle className="font-display font-black text-2xl tracking-tighter">
                  How can we help?
                </SheetTitle>
                <SheetDescription className="text-xs text-neutral-600 mt-1">
                  Search the FAQ, browse articles, or take a guided tour.
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-0 border-b border-neutral-200">
              <button
                type="button"
                onClick={() => setStory(true)}
                data-testid="help-action-story"
                className="p-5 text-left border-r border-neutral-200 hover:bg-neutral-50 group"
              >
                <PlayCircle className="w-5 h-5 mb-2 text-[#7C5FB0]" />
                <div className="text-sm font-bold">Watch the intro</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  6 scenes · ~45 sec
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTour(true)}
                data-testid="help-action-tour"
                className="p-5 text-left hover:bg-neutral-50 group"
              >
                <Compass className="w-5 h-5 mb-2 text-[#4A4458]" />
                <div className="text-sm font-bold">Take the tour</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  6 quick steps · ~90 sec
                </div>
              </button>
            </div>

            {/* Search */}
            <div className="p-5 border-b border-neutral-200">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search FAQ…"
                  data-testid="help-search-input"
                  className="w-full h-10 pl-9 pr-3 text-sm border border-neutral-300 focus:border-neutral-900 outline-none bg-white"
                />
              </div>
            </div>

            {/* FAQ list */}
            <ul className="divide-y divide-neutral-100" data-testid="help-faq-list">
              {filtered.length === 0 ? (
                <li className="px-5 py-8 text-sm text-neutral-500 text-center">
                  No matches. Try the full Knowledge Base →
                </li>
              ) : (
                filtered.map((f, i) => {
                  const isOpen = openFaq === i;
                  return (
                    <li key={i} data-testid={`help-faq-${i}`}>
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : i)}
                        className="w-full text-left px-5 py-4 hover:bg-neutral-50 flex items-start gap-2"
                      >
                        <ChevronRight
                          className={`w-4 h-4 text-neutral-400 flex-shrink-0 mt-0.5 transition-transform ${
                            isOpen ? "rotate-90" : ""
                          }`}
                        />
                        <span className="text-sm font-medium leading-snug">{f.q}</span>
                      </button>
                      {isOpen && (
                        <div className="px-12 pb-4 text-sm text-neutral-700 leading-relaxed whitespace-pre-line">
                          {f.a}
                        </div>
                      )}
                    </li>
                  );
                })
              )}
            </ul>

            {/* Link to full Help page */}
            <Link
              to="/help"
              onClick={() => onOpenChange?.(false)}
              data-testid="help-fullpage-link"
              className="flex items-center justify-between px-5 py-4 border-t-2 border-neutral-900 bg-neutral-900 text-white hover:bg-[#7C5FB0] group"
            >
              <span className="flex items-center gap-3">
                <BookOpen className="w-5 h-5" />
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] font-bold">
                    Open full Help center
                  </span>
                  <span className="block text-[11px] text-neutral-400 group-hover:text-white/80 mt-0.5">
                    Knowledge Base · Demo · Full FAQ
                  </span>
                </span>
              </span>
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </SheetContent>
      </Sheet>

      <IntroStory
        open={story}
        onClose={() => setStory(false)}
        onPrimaryCta={() => setStory(false)}
      />
      <InteractiveTour open={tour} onClose={() => setTour(false)} />
    </>
  );
}
